/**
 * Cases Page
 *
 * Support case management for feedback + Flockbot support threads.
 * Unified case queue with an inline conversation panel beneath the selected
 * case row.
 */

import { Fragment, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { feedbackAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import { publishUntriagedCaseCount } from '../utils/caseStatsEvents';
import PageHeader from '../components/analytics/PageHeader';
import {
  ChatBubbleLeftEllipsisIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  XMarkIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  InboxIcon,
  FolderOpenIcon,
} from '@heroicons/react/24/outline';

const MAX_REPLY_LENGTH = 4000;

const STATUS_LABELS = {
  new: 'New',
  acknowledged: 'Acknowledged',
  in_progress: 'In Progress',
  closed: 'Closed',
  dismissed: 'Dismissed',
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'closed', label: 'Closed' },
  { value: 'dismissed', label: 'Dismissed' },
];

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));

const CATEGORY_OPTIONS = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature_request', label: 'Feature' },
  { value: 'usability', label: 'Usability' },
  { value: 'question', label: 'Question' },
  { value: 'praise', label: 'Praise' },
];

const URGENCY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const CATEGORY_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'bug', label: 'Bugs' },
  { value: 'feature_request', label: 'Features' },
  { value: 'usability', label: 'Usability' },
  { value: 'question', label: 'Questions' },
  { value: 'praise', label: 'Praise' },
];

const URGENCY_FILTERS = [
  { value: 'all', label: 'All Urgency' },
  { value: 'critical', label: 'Critical' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const QUEUE_VIEW_LABELS = {
  untriaged: 'Untriaged',
  open: 'Open Cases',
  needs_reply: 'Needs Reply',
  critical: 'Critical Open',
};

// Format time ago
function formatTimeAgo(dateString) {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  return 'Just now';
}

function formatDeviceInfo(deviceInfo) {
  if (!deviceInfo) return null;
  let info = deviceInfo;
  if (typeof info === 'string') {
    try {
      info = JSON.parse(info);
    } catch {
      return info;
    }
  }
  if (typeof info !== 'object' || Array.isArray(info)) return String(info);

  const preferredKeys = ['deviceName', 'brand', 'modelName', 'model', 'osName', 'os', 'osVersion'];
  const parts = preferredKeys
    .map((key) => info[key])
    .filter((value, index, values) => value != null && value !== '' && values.indexOf(value) === index)
    .map(String);
  return parts.length > 0 ? parts.join(' · ') : JSON.stringify(info);
}

function formatDateTime(dateString) {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? String(dateString) : date.toLocaleString();
}

// Platform icon component
function PlatformIcon({ platform }) {
  if (platform === 'ios' || platform === 'android') {
    return <DevicePhoneMobileIcon className="w-4 h-4" />;
  }
  return <ComputerDesktopIcon className="w-4 h-4" />;
}

// Status badge component
function StatusBadge({ status }) {
  const styles = {
    new: 'bg-blue-100 text-blue-800',
    acknowledged: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-purple-100 text-purple-800',
    closed: 'bg-green-100 text-green-800',
    dismissed: 'bg-gray-100 text-gray-600',
  };

  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status] || styles.new}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// AI Category badge component
function CategoryBadge({ category }) {
  if (!category) return null;

  const styles = {
    bug: 'bg-red-100 text-red-800',
    feature_request: 'bg-purple-100 text-purple-800',
    usability: 'bg-orange-100 text-orange-800',
    praise: 'bg-green-100 text-green-800',
    question: 'bg-blue-100 text-blue-800',
  };

  const labels = {
    bug: 'Bug',
    feature_request: 'Feature',
    usability: 'Usability',
    praise: 'Praise',
    question: 'Question',
  };

  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[category] || 'bg-gray-100 text-gray-600'}`}>
      {labels[category] || category}
    </span>
  );
}

// AI Urgency badge component
function UrgencyBadge({ urgency }) {
  if (!urgency || urgency === 'low') return null;

  const styles = {
    critical: 'bg-red-500 text-white',
    medium: 'bg-yellow-100 text-yellow-800',
  };

  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[urgency]}`}>
      {urgency === 'critical' ? 'Critical' : 'Medium'}
    </span>
  );
}

function ClassificationSelect({ label, value, options, kind, onChange, disabled, describedBy }) {
  const categoryStyles = {
    bug: 'bg-red-100 text-red-800',
    feature_request: 'bg-purple-100 text-purple-800',
    usability: 'bg-orange-100 text-orange-800',
    praise: 'bg-green-100 text-green-800',
    question: 'bg-blue-100 text-blue-800',
  };
  const urgencyStyles = {
    critical: 'bg-red-500 text-white',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-gray-100 text-gray-700',
  };
  const statusStyles = {
    new: 'bg-blue-100 text-blue-800',
    acknowledged: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-purple-100 text-purple-800',
    closed: 'bg-green-100 text-green-800',
    dismissed: 'bg-gray-100 text-gray-600',
  };
  const valueStyles = kind === 'category'
    ? categoryStyles
    : kind === 'urgency'
      ? urgencyStyles
      : statusStyles;
  return (
    <span className={`relative inline-flex h-5 items-center rounded-full focus-within:ring-2 focus-within:ring-storm-blue focus-within:ring-offset-1 ${disabled ? 'opacity-60' : ''}`}>
      <select
        aria-label={label}
        aria-describedby={describedBy}
        title={`${label} — click to reclassify`}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className={`h-5 cursor-pointer appearance-none rounded-full border-0 py-0 pl-2 pr-6 text-xs font-medium leading-5 focus:outline-none disabled:cursor-wait ${valueStyles[value] || 'bg-gray-100 text-gray-700'}`}
      >
        {!value && <option value="" disabled>Unclassified</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <ChevronDownIcon
        className="pointer-events-none absolute right-1.5 h-3 w-3 opacity-70"
        aria-hidden="true"
      />
    </span>
  );
}

function SentimentIndicator({ sentiment }) {
  if (!sentiment) return null;
  const colors = {
    positive: 'bg-green-500',
    neutral: 'bg-amber-400',
    negative: 'bg-red-500',
  };

  return (
    <span
      className={`h-2.5 w-2.5 rounded-full ${colors[sentiment] || 'bg-gray-400'}`}
      aria-hidden="true"
    />
  );
}

// Source badge — only legacy_form gets a visible hint (replies go out by email)
function SourceBadge({ source }) {
  if (source !== 'legacy_form') return null;
  return (
    <span
      className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 flex items-center gap-1"
      title="Submitted via the old feedback form — replies also go out by email"
    >
      <EnvelopeIcon className="w-3 h-3" />
      email-only
    </span>
  );
}

function FilterPill({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-touch rounded-full border px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue focus-visible:ring-offset-2 ${
        active
          ? 'border-deep-navy bg-deep-navy text-white shadow-sm'
          : 'border-gray-200 bg-white text-gray-600 hover:border-sky-blue hover:bg-gray-50 hover:text-deep-navy'
      }`}
    >
      {label}
      {Number.isFinite(count) && (
        <span className={`ml-1.5 ${active ? 'text-cloud-white' : 'text-gray-400'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function QueueStatCard({ children, count, active, tone = 'neutral', icon: Icon, onClick }) {
  const hasAlert = count > 0 && (tone === 'warning' || tone === 'danger');
  const baseTone = hasAlert
    ? tone === 'danger'
      ? 'border-red-200 bg-red-50 text-red-900'
      : 'border-amber-200 bg-amber-50 text-amber-900'
    : 'border-gray-200 bg-white text-deep-navy';
  const activeTone = active
    ? tone === 'danger' && hasAlert
      ? 'border-red-400 bg-red-100 ring-red-200'
      : tone === 'warning' && hasAlert
        ? 'border-amber-400 bg-amber-100 ring-amber-200'
        : 'border-storm-blue bg-blue-50 ring-blue-100'
    : '';
  const iconTone = hasAlert
    ? tone === 'danger'
      ? 'bg-red-100 text-red-600'
      : 'bg-amber-100 text-amber-700'
    : active
      ? 'bg-white text-storm-blue'
      : 'bg-gray-100 text-storm-blue';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      disabled={!Number.isFinite(count)}
      className={`grid min-h-[72px] min-w-[180px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2.5 rounded-xl border px-3 text-left shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 ${baseTone} ${activeTone} ${active ? 'ring-2' : ''}`}
    >
      <span className={`grid h-9 w-9 place-items-center rounded-lg ${iconTone}`} aria-hidden="true">
        {Icon ? <Icon className="h-[18px] w-[18px]" /> : null}
      </span>
      <span className="truncate text-sm font-semibold">{children}</span>
      <span className="text-2xl font-bold tracking-tight">{Number.isFinite(count) ? count : '—'}</span>
    </button>
  );
}

// Case Queue Row
function CaseRow({ caseItem, onSelect, isExpanded, panelId }) {
  const lastActivity = caseItem.last_message_at || caseItem.created_at;

  return (
    <button
      type="button"
      id={`case-row-${caseItem.id}`}
      onClick={() => onSelect(isExpanded ? null : caseItem)}
      aria-expanded={isExpanded}
      aria-controls={panelId}
      className={`w-full min-h-touch border-b px-4 py-4 text-left transition-colors focus:outline-none focus-visible:relative focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-storm-blue ${
        isExpanded
          ? 'border-blue-200 bg-blue-50'
          : caseItem.awaiting_reply
            ? 'border-amber-100 bg-amber-50/70 hover:bg-amber-50'
            : 'border-gray-100 bg-white hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Header: case number, awaiting dot, status, badges */}
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="text-sm font-bold text-gray-900">
              #{caseItem.case_number ?? '—'}
            </span>
            {caseItem.awaiting_reply && (
              <span className="inline-flex items-center" title="Awaiting reply from Flock team">
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500" aria-hidden="true" />
                <span className="sr-only">Awaiting reply from Flock team</span>
              </span>
            )}
            <StatusBadge status={caseItem.status} />
            <CategoryBadge category={caseItem.ai_category} />
            <UrgencyBadge urgency={caseItem.ai_urgency} />
            <SourceBadge source={caseItem.source} />
          </div>

          {/* Message snippet (2 lines) */}
          <p className="text-gray-800 text-sm line-clamp-2">
            {caseItem.message}
          </p>

          {/* User / org / platform / activity */}
          <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500 mt-2">
            <span>{caseItem.user_name || 'Unknown'}</span>
            <span>&bull;</span>
            <span>{caseItem.organization_name || 'Unknown Org'}</span>
            {caseItem.platform && (
              <>
                <span>&bull;</span>
                <span className="flex items-center gap-1">
                  <PlatformIcon platform={caseItem.platform} />
                  {caseItem.platform}
                </span>
              </>
            )}
            <span>&bull;</span>
            <span className="text-gray-400">{formatTimeAgo(lastActivity)}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

// System message caption — renders status changes / case lifecycle nicely
function SystemMessage({ message }) {
  const type = message.metadata?.type;
  let caption = message.content;

  if (!caption) {
    if (type === 'status_change') {
      const to = message.metadata?.to || message.metadata?.status;
      caption = to ? `Status changed to ${STATUS_LABELS[to] || to}` : 'Status changed';
    } else if (type === 'case_created') {
      caption = 'Support case opened';
    } else if (type === 'case_offer') {
      caption = 'Flockbot offered to open a support case';
    } else {
      caption = 'System event';
    }
  }

  return (
    <div className="text-center py-1">
      <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full inline-block">
        {caption}
        <span className="ml-2 text-gray-300">{formatTimeAgo(message.created_at)}</span>
      </span>
    </div>
  );
}

// Single thread message bubble
function ThreadMessage({ message }) {
  if (message.role === 'system') {
    return <SystemMessage message={message} />;
  }

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-blue-600 text-white rounded-2xl rounded-br-sm px-4 py-2">
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          <p className="text-[10px] text-blue-200 mt-1 text-right">
            {formatTimeAgo(message.created_at)}
          </p>
        </div>
      </div>
    );
  }

  if (message.role === 'team') {
    return (
      <div className="flex justify-start">
        <div className="max-w-[80%] bg-white border-l-4 border-l-green-500 border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-2 shadow-sm">
          <p className="text-xs font-semibold text-green-700 mb-0.5">
            {message.author_name || 'Flock Team'}
          </p>
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{message.content}</p>
          <p className="text-[10px] text-gray-400 mt-1">{formatTimeAgo(message.created_at)}</p>
        </div>
      </div>
    );
  }

  // assistant (Flockbot) — muted
  return (
    <div className="flex justify-start">
      <div className="max-w-[80%] bg-gray-50 border border-gray-100 rounded-2xl rounded-bl-sm px-4 py-2">
        <p className="text-xs font-medium text-gray-400 mb-0.5">Flockbot</p>
        <p className="text-sm text-gray-600 whitespace-pre-wrap">{message.content}</p>
        <p className="text-[10px] text-gray-300 mt-1">{formatTimeAgo(message.created_at)}</p>
      </div>
    </div>
  );
}

// Thread view — chronological messages with loading/error/empty states
function ThreadView({ threadState, onRetry }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [threadState.messages]);

  if (threadState.loading) {
    return (
      <div className="p-6 space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className={`flex ${i % 2 ? 'justify-end' : 'justify-start'}`}>
            <div className="h-14 bg-gray-100 rounded-2xl w-2/3 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (threadState.error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 text-sm">{threadState.error}</p>
        <button
          onClick={onRetry}
          className="mt-2 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (threadState.isNull) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500 text-sm">No thread yet (legacy row)</p>
        <p className="text-gray-400 text-xs mt-1">
          Sending a reply will start a conversation thread for this case.
        </p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="p-4 space-y-3 max-h-[520px] overflow-y-auto bg-gray-50/70">
      {threadState.messages.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-4">No messages in this thread</p>
      ) : (
        threadState.messages.map((msg) => <ThreadMessage key={msg.id} message={msg} />)
      )}
    </div>
  );
}

// Reply composer — administrator identity comes from the verified Fold session.
function ReplyComposer({ onSend, sending, disabled }) {
  const [message, setMessage] = useState('');

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || sending) return;
    const ok = await onSend(trimmed);
    if (ok) setMessage('');
  };

  return (
    <div className="border-t border-gray-100 p-4 space-y-2">
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, MAX_REPLY_LENGTH))}
        placeholder={disabled ? 'Replying is disabled for this case' : 'Write a reply to the user...'}
        rows={3}
        disabled={disabled || sending}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:bg-gray-50 disabled:text-gray-400"
      />
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`text-xs ${message.length >= MAX_REPLY_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
            {message.length}/{MAX_REPLY_LENGTH}
          </span>
        </div>
        <button
          onClick={handleSend}
          disabled={disabled || sending || !message.trim()}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <PaperAirplaneIcon className="w-4 h-4" />
          {sending ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// Internal notes + GitHub issue URL (never shown to user)
function InternalSection({ caseItem, onSave, saving }) {
  const [notes, setNotes] = useState(caseItem.notes || '');
  const [githubUrl, setGithubUrl] = useState(caseItem.github_issue_url || '');

  // Reset when switching cases
  useEffect(() => {
    setNotes(caseItem.notes || '');
    setGithubUrl(caseItem.github_issue_url || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseItem.id]);

  const hasChanges =
    notes !== (caseItem.notes || '') || githubUrl !== (caseItem.github_issue_url || '');

  const handleSave = () => {
    const updates = {};
    if (notes !== (caseItem.notes || '')) updates.notes = notes;
    if (githubUrl !== (caseItem.github_issue_url || '')) {
      updates.github_issue_url = githubUrl || null;
    }
    onSave(updates);
  };

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-sm font-semibold text-amber-900">Internal only</p>
        <p className="mt-0.5 text-xs text-amber-700">Notes and issue links are never shown to the member.</p>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-500">Internal Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Only visible to the Flock team..."
          rows={5}
          className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-500">GitHub Issue URL</label>
        <input
          type="url"
          value={githubUrl}
          onChange={(e) => setGithubUrl(e.target.value)}
          placeholder="https://github.com/Covenant-Labs-LLC/Flock/issues/123"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="min-h-touch rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save internal details'}
        </button>
      </div>
    </div>
  );
}

// Case Panel — persistent case actions with Thread, Details, and Internal tabs
function CasePanel({
  caseItem,
  panelId,
  threadState,
  onRetryThread,
  onReply,
  sending,
  onStatusChange,
  onClassificationChange,
  onSaveInternal,
  saving,
  onClose,
  userDeletedOverride,
}) {
  const [activeTab, setActiveTab] = useState('thread');
  const userDeleted = !caseItem.user_id || userDeletedOverride;
  const deviceInfo = formatDeviceInfo(caseItem.device_info);
  const rawDeviceInfo = caseItem.device_info
    ? typeof caseItem.device_info === 'string'
      ? caseItem.device_info
      : JSON.stringify(caseItem.device_info, null, 2)
    : null;
  const tabIds = ['thread', 'details', 'internal'];

  const handleTabKeyDown = (event, currentTab) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = tabIds.indexOf(currentTab);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabIds.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabIds.length) % tabIds.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabIds.length - 1;
    const nextTab = tabIds[nextIndex];
    setActiveTab(nextTab);
    window.requestAnimationFrame(() => {
      document.getElementById(`case-tab-${nextTab}-${caseItem.id}`)?.focus();
    });
  };

  const tabClassName = (tab) => `relative min-h-touch whitespace-nowrap px-4 py-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-storm-blue ${
    activeTab === tab
      ? 'text-deep-navy after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:bg-storm-blue'
      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
  }`;

  return (
    <div
      id={panelId}
      role="region"
      aria-labelledby={`case-row-${caseItem.id}`}
      className="overflow-hidden border-b border-blue-200 bg-white shadow-inner animate-fadeIn motion-reduce:animate-none"
    >
      <div className="border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white px-4 py-1 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold text-gray-900">#{caseItem.case_number ?? '—'}</h3>
            <StatusBadge status={caseItem.status} />
            <ClassificationSelect
              label="Case category"
              value={caseItem.ai_category}
              options={CATEGORY_OPTIONS}
              kind="category"
              onChange={(value) => onClassificationChange('ai_category', value)}
              disabled={saving}
            />
            <ClassificationSelect
              label="Case urgency"
              value={caseItem.ai_urgency}
              options={URGENCY_OPTIONS}
              kind="urgency"
              onChange={(value) => onClassificationChange('ai_urgency', value)}
              disabled={saving}
            />
            {caseItem.github_issue_url && (
              <a
                href={caseItem.github_issue_url}
                target="_blank"
                rel="noopener noreferrer"
                title="Open GitHub issue"
                className="rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue"
                onClick={(event) => event.stopPropagation()}
              >
                <LinkIcon className="h-4 w-4 text-gray-400 hover:text-blue-600" />
              </a>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 text-xs font-medium text-gray-500">
                Status
              </span>
              <ClassificationSelect
                label="Case status"
                value={caseItem.status}
                options={STATUS_OPTIONS}
                kind="status"
                onChange={onStatusChange}
                disabled={saving}
                describedBy={`case-status-warning-${caseItem.id}`}
              />
              <span id={`case-status-warning-${caseItem.id}`} className="hidden text-xs text-gray-400 xl:inline">
                notifies user
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-5 items-center justify-center gap-1 rounded px-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-white hover:text-deep-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue"
            >
              <ChevronUpIcon className="h-3 w-3" />
              Collapse
            </button>
          </div>
        </div>
      </div>

      {caseItem.source === 'legacy_form' && (
        <div className="flex items-center gap-1.5 border-b border-blue-100 bg-blue-50 px-4 py-1">
          <EnvelopeIcon className="h-3.5 w-3.5 shrink-0 text-blue-500" />
          <p className="text-xs text-blue-800">User is on an old app build — replies also go out by email.</p>
        </div>
      )}
      {userDeleted && (
        <div className="flex items-center gap-2 border-b border-red-100 bg-red-50 px-4 py-2">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-500" />
          <p className="text-xs text-red-800">
            User deleted their account — cannot reply in-thread.
            {caseItem.user_email && <> Snapshot email: <span className="font-medium">{caseItem.user_email}</span></>}
          </p>
        </div>
      )}

      <div role="tablist" aria-label={`Case #${caseItem.case_number ?? 'details'}`} className="flex overflow-x-auto border-b border-gray-200 bg-white px-2 sm:px-4">
        <button
          type="button"
          role="tab"
          id={`case-tab-thread-${caseItem.id}`}
          aria-selected={activeTab === 'thread'}
          aria-controls={`case-tabpanel-thread-${caseItem.id}`}
          tabIndex={activeTab === 'thread' ? 0 : -1}
          onClick={() => setActiveTab('thread')}
          onKeyDown={(event) => handleTabKeyDown(event, 'thread')}
          className={tabClassName('thread')}
        >Thread</button>
        <button
          type="button"
          role="tab"
          id={`case-tab-details-${caseItem.id}`}
          aria-selected={activeTab === 'details'}
          aria-controls={`case-tabpanel-details-${caseItem.id}`}
          tabIndex={activeTab === 'details' ? 0 : -1}
          onClick={() => setActiveTab('details')}
          onKeyDown={(event) => handleTabKeyDown(event, 'details')}
          className={tabClassName('details')}
        >Details</button>
        <button
          type="button"
          role="tab"
          id={`case-tab-internal-${caseItem.id}`}
          aria-selected={activeTab === 'internal'}
          aria-controls={`case-tabpanel-internal-${caseItem.id}`}
          tabIndex={activeTab === 'internal' ? 0 : -1}
          onClick={() => setActiveTab('internal')}
          onKeyDown={(event) => handleTabKeyDown(event, 'internal')}
          className={tabClassName('internal')}
        >Internal</button>
      </div>

      <div
        role="tabpanel"
        id={`case-tabpanel-thread-${caseItem.id}`}
        aria-labelledby={`case-tab-thread-${caseItem.id}`}
        hidden={activeTab !== 'thread'}
      >
        <ThreadView threadState={threadState} onRetry={onRetryThread} />
        <ReplyComposer
          key={`reply-${caseItem.id}`}
          onSend={onReply}
          sending={sending}
          disabled={userDeleted || threadState.loading}
        />
      </div>

      <div
        role="tabpanel"
        id={`case-tabpanel-details-${caseItem.id}`}
        aria-labelledby={`case-tab-details-${caseItem.id}`}
        hidden={activeTab !== 'details'}
        className="p-4 sm:p-6"
      >
        <div className="grid gap-5 lg:grid-cols-2">
          <section className="rounded-xl border border-gray-200 bg-white p-4" aria-labelledby={`member-details-${caseItem.id}`}>
            <h4 id={`member-details-${caseItem.id}`} className="text-sm font-semibold text-gray-900">Member and app</h4>
            <dl className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium text-gray-500">User</dt>
                <dd className="mt-1 font-medium text-gray-900">{caseItem.user_name || 'Unknown'}</dd>
                <dd className="mt-0.5 break-all text-xs text-gray-500">{caseItem.user_email || 'No email'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Role</dt>
                <dd className="mt-1 text-gray-800">{caseItem.user_role || 'Unknown'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Organization</dt>
                <dd className="mt-1 text-gray-800">{caseItem.organization_name || 'Unknown'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Platform</dt>
                <dd className="mt-1 flex items-center gap-1.5 text-gray-800">
                  <PlatformIcon platform={caseItem.platform} />
                  <span>{caseItem.platform || 'Unknown'}{caseItem.app_version ? ` v${caseItem.app_version}` : ''}</span>
                </dd>
              </div>
            </dl>
          </section>

          <section className="rounded-xl border border-gray-200 bg-gray-50/70 p-4" aria-labelledby={`device-info-${caseItem.id}`}>
            <h4 id={`device-info-${caseItem.id}`} className="text-sm font-semibold text-gray-900">Device Info</h4>
            {deviceInfo && <p className="mt-2 text-xs text-gray-500">{deviceInfo}</p>}
            {rawDeviceInfo ? (
              <pre className="mt-3 max-h-48 overflow-auto rounded-lg bg-white p-3 text-xs text-gray-700 ring-1 ring-gray-200">{rawDeviceInfo}</pre>
            ) : (
              <p className="mt-3 text-sm text-gray-400">No device metadata captured.</p>
            )}
          </section>

          <section className="rounded-xl border border-purple-100 bg-gradient-to-r from-purple-50 to-blue-50 p-4 lg:col-span-2" aria-labelledby={`ai-analysis-${caseItem.id}`}>
            <h4 id={`ai-analysis-${caseItem.id}`} className="flex items-center gap-2 text-sm font-semibold text-purple-900">
              <SparklesIcon className="h-4 w-4" />
              AI Analysis
            </h4>
            {caseItem.ai_processed_at ? (
              <dl className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500">Category</dt>
                  <dd className="mt-1"><CategoryBadge category={caseItem.ai_category} /></dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">Sentiment</dt>
                  <dd className="mt-1 flex items-center gap-2 text-sm capitalize text-gray-700">
                    <SentimentIndicator sentiment={caseItem.ai_sentiment} />
                    {caseItem.ai_sentiment || 'Unknown'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">Urgency</dt>
                  <dd className="mt-1 text-sm capitalize text-gray-700">{caseItem.ai_urgency || 'Unknown'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">Confidence</dt>
                  <dd className="mt-1 text-sm text-gray-700">
                    {caseItem.ai_confidence != null ? `${Math.round(Number(caseItem.ai_confidence) * 100)}%` : 'N/A'}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="mt-3 text-sm text-purple-700">AI analysis is still processing.</p>
            )}
          </section>

          <dl className="grid gap-2 border-t border-gray-100 pt-4 text-xs text-gray-500 sm:grid-cols-2 lg:col-span-2">
            <div><dt className="font-medium text-gray-400">Created</dt><dd className="mt-0.5">{formatDateTime(caseItem.created_at)}</dd></div>
            <div><dt className="font-medium text-gray-400">Updated</dt><dd className="mt-0.5">{formatDateTime(caseItem.updated_at)}</dd></div>
          </dl>
        </div>
      </div>

      <div
        role="tabpanel"
        id={`case-tabpanel-internal-${caseItem.id}`}
        aria-labelledby={`case-tab-internal-${caseItem.id}`}
        hidden={activeTab !== 'internal'}
      >
        <InternalSection
          key={`internal-${caseItem.id}`}
          caseItem={caseItem}
          onSave={onSaveInternal}
          saving={saving}
        />
      </div>
    </div>
  );
}

export default function CasesPage() {
  const { currentEnv } = useEnvironment();
  const [caseList, setCaseList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Load-more failures show a dismissible inline banner instead of the
  // full-page error takeover (which would lose the open case panel).
  const [appendError, setAppendError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  // Selected case for the right panel
  const [selectedCase, setSelectedCase] = useState(null);
  // Set when a reply 409s with USER_DELETED (row data may be stale)
  const [deletedUserCaseIds, setDeletedUserCaseIds] = useState(new Set());

  // Thread state for the selected case
  const [threadState, setThreadState] = useState({
    loading: false,
    error: null,
    isNull: false,
    conversation: null,
    messages: [],
  });
  const threadFetchSeqRef = useRef(0);
  const selectedCaseIdRef = useRef(null);

  const selectCase = useCallback((caseItem) => {
    const nextId = caseItem?.id ?? null;
    const selectionChanged = selectedCaseIdRef.current !== nextId;
    selectedCaseIdRef.current = nextId;
    if (selectionChanged) {
      threadFetchSeqRef.current += 1;
      setThreadState({
        loading: !!caseItem,
        error: null,
        isNull: false,
        conversation: null,
        messages: [],
      });
    }
    setSelectedCase(caseItem);
  }, []);

  // The environment switch changes the API base URL. Clear the old selection
  // before paint so an old-env case can never be replied to through the new
  // environment, and invalidate any thread response still in flight.
  useLayoutEffect(() => {
    selectCase(null);
  }, [currentEnv, selectCase]);

  // Filters + sort
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  // Debounced copy of the search box — the fetch effect keys off this, so
  // typing doesn't spam the API and mount doesn't double-fetch.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortMode, setSortMode] = useState('awaiting_first');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [queueView, setQueueView] = useState('all');

  const activeFilterSummary = [
    queueView !== 'all' ? QUEUE_VIEW_LABELS[queueView] : null,
    statusFilter !== 'all' ? STATUS_FILTERS.find((option) => option.value === statusFilter)?.label : null,
    categoryFilter !== 'all' ? CATEGORY_FILTERS.find((option) => option.value === categoryFilter)?.label : null,
    urgencyFilter !== 'all' ? URGENCY_FILTERS.find((option) => option.value === urgencyFilter)?.label : null,
    searchTerm.trim() ? `“${searchTerm.trim()}”` : null,
    sortMode === 'newest' ? 'Newest first' : 'Awaiting reply first',
  ].filter(Boolean);

  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 20;

  // Monotonic fetch sequence — a response is discarded (no setState at all) if
  // a newer fetch started while it was in flight, so a slow load-more can't
  // land after a filter reset and append stale rows.
  const fetchSeqRef = useRef(0);

  // Load-more passes the page explicitly — reading `page` from the state
  // closure after setPage would re-fetch the stale page and duplicate rows.
  const fetchData = useCallback(async ({ reset = false, page: pageOverride } = {}) => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setAppendError(null);
    try {
      const currentPage = reset ? 1 : (pageOverride ?? page);
      if (reset) setPage(1);

      const params = {
        page: currentPage,
        limit: pageSize,
        sort: sortMode,
      };
      if (queueView !== 'all') params.queue_view = queueView;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.ai_category = categoryFilter;
      if (urgencyFilter !== 'all') params.ai_urgency = urgencyFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const [listRes, statsRes] = await Promise.all([
        feedbackAPI.list(params),
        feedbackAPI.getStats(),
      ]);
      if (seq !== fetchSeqRef.current) return false; // stale — a newer fetch owns the UI

      const newCases = listRes.data?.data?.feedback || [];
      const pagination = listRes.data?.data?.pagination || {};

      if (reset) {
        setCaseList(newCases);
        const selectedId = selectedCaseIdRef.current;
        if (selectedId && !newCases.some((caseItem) => caseItem.id === selectedId)) {
          selectCase(null);
        }
      } else {
        setCaseList((prev) => [...prev, ...newCases]);
      }

      const freshStats = statsRes.data?.data || null;
      setHasMore(pagination.hasMore || false);
      setStats(freshStats);
      publishUntriagedCaseCount(freshStats?.byStatus?.new ?? 0);
      return true;
    } catch (err) {
      if (seq !== fetchSeqRef.current) return false;
      console.error('[CasesPage] Error fetching data:', err);
      const message = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load data';
      if (reset) {
        setError(message);
      } else {
        // Keep the page (and the open case panel) — banner above the list
        setAppendError(message);
      }
      return false;
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [page, queueView, statusFilter, categoryFilter, urgencyFilter, debouncedSearch, sortMode, selectCase]);

  useEffect(() => {
    fetchData({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEnv, queueView, statusFilter, categoryFilter, urgencyFilter, sortMode, debouncedSearch]);

  // Debounce the search box; on mount this sets '' → '' (no change, no refetch)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch thread whenever the selected case changes
  const fetchThread = useCallback(async (caseId) => {
    if (selectedCaseIdRef.current !== caseId) return;
    const seq = ++threadFetchSeqRef.current;
    setThreadState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await feedbackAPI.getThread(caseId);
      if (seq !== threadFetchSeqRef.current || selectedCaseIdRef.current !== caseId) return;
      const thread = res.data?.data?.thread;
      if (!thread) {
        setThreadState({ loading: false, error: null, isNull: true, conversation: null, messages: [] });
      } else {
        setThreadState({
          loading: false,
          error: null,
          isNull: false,
          conversation: thread.conversation,
          messages: thread.messages || [],
        });
      }
    } catch (err) {
      if (seq !== threadFetchSeqRef.current || selectedCaseIdRef.current !== caseId) return;
      console.error('[CasesPage] Error fetching thread:', err);
      setThreadState({
        loading: false,
        error: err.response?.data?.message || err.message || 'Failed to load thread',
        isNull: false,
        conversation: null,
        messages: [],
      });
    }
  }, []);

  useEffect(() => {
    if (selectedCase?.id) {
      fetchThread(selectedCase.id);
    } else {
      threadFetchSeqRef.current += 1;
    }
  }, [selectedCase?.id, fetchThread]);

  useEffect(() => () => {
    threadFetchSeqRef.current += 1;
    selectedCaseIdRef.current = null;
  }, []);

  // Merge updates into both the list row and the selected case
  const mergeCaseUpdate = (id, updates) => {
    setCaseList((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
    setSelectedCase((prev) => (prev?.id === id ? { ...prev, ...updates } : prev));
  };

  // Send a team reply
  const handleReply = async (message) => {
    if (!selectedCase) return false;
    const caseId = selectedCase.id;
    setSending(true);
    try {
      const res = await feedbackAPI.reply(caseId, message);
      const sent = res.data?.data?.message;
      const updatedCase = res.data?.data?.case;

      if (sent && selectedCaseIdRef.current === caseId) {
        // A thread request that started before this mutation must never replace
        // the newly-sent reply with its older message snapshot.
        threadFetchSeqRef.current += 1;
        // Append optimistically in the thread shape (snake_case fields)
        setThreadState((prev) => ({
          ...prev,
          loading: false,
          error: null,
          isNull: false,
          messages: [
            ...prev.messages,
            {
              id: sent.id,
              role: 'team',
              content: sent.content,
              author_name: sent.authorName,
              metadata: null,
              created_at: sent.createdAt,
            },
          ],
        }));
      }
      if (sent) {
        mergeCaseUpdate(caseId, {
          awaiting_reply: false,
          last_message_at: sent.createdAt,
          ...(updatedCase?.status ? { status: updatedCase.status } : {}),
          ...(updatedCase?.conversationId ? { conversation_id: updatedCase.conversationId } : {}),
        });
        await fetchData({ reset: true });
      }
      return true;
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.code === 'USER_DELETED') {
        setDeletedUserCaseIds((prev) => new Set(prev).add(caseId));
      } else {
        console.error('[CasesPage] Error sending reply:', err);
        alert(err.response?.data?.message || 'Failed to send reply');
      }
      return false;
    } finally {
      setSending(false);
    }
  };

  // PATCH helper (status / notes / github_issue_url / internal classification)
  const handlePatch = async (id, updates, { refetchThread = false } = {}) => {
    setSaving(true);
    try {
      const res = await feedbackAPI.update(id, updates);
      const updated = res.data?.data;
      mergeCaseUpdate(id, updated || updates);

      const classificationChanged =
        updates.ai_category !== undefined || updates.ai_urgency !== undefined;

      // A classification can change active queue membership and critical stats.
      if (classificationChanged) {
        await fetchData({ reset: true });
      } else if (updates.status) {
        await fetchData({ reset: true });
      }

      // Status changes append a system message server-side — refetch to show it
      if (refetchThread && selectedCaseIdRef.current === id) {
        await fetchThread(id);
      }
    } catch (err) {
      console.error('[CasesPage] Error updating case:', err);
      alert(err.response?.data?.message || 'Failed to update case');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    if (!selectedCase || newStatus === selectedCase.status) return;
    const confirmed = window.confirm(
      `Change status to "${STATUS_LABELS[newStatus] || newStatus}"? This notifies the user (push + email).`
    );
    if (!confirmed) return;
    handlePatch(selectedCase.id, { status: newStatus }, { refetchThread: true });
  };

  const handleClassificationChange = (field, value) => {
    if (!selectedCase || selectedCase[field] === value) return;
    handlePatch(selectedCase.id, { [field]: value });
  };

  const handleSaveInternal = (updates) => {
    if (!selectedCase || Object.keys(updates).length === 0) return;
    handlePatch(selectedCase.id, updates);
  };

  // Advance the page only on success so a failed append can be retried
  // without skipping a page.
  const handleLoadMore = async () => {
    const next = page + 1;
    const ok = await fetchData({ page: next });
    if (ok) setPage(next);
  };

  const handleCollapseCase = () => {
    const caseId = selectedCaseIdRef.current;
    selectCase(null);
    if (caseId) {
      window.requestAnimationFrame(() => {
        document.getElementById(`case-row-${caseId}`)?.focus();
      });
    }
  };

  const handleQueueViewChange = (nextView) => {
    const resolvedView = queueView === nextView ? 'all' : nextView;
    setQueueView(resolvedView);
    if (resolvedView !== 'all') {
      setStatusFilter('all');
      setUrgencyFilter('all');
    }
  };

  if (error) {
    return (
      <div>
        <PageHeader title="Cases" />
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading cases</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={() => fetchData({ reset: true })}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Cases"
        description="Support cases from Flockbot and the feedback form"
        onRefresh={() => fetchData({ reset: true })}
        loading={loading}
      />

      <section
        className="grid grid-cols-[repeat(4,minmax(180px,1fr))] gap-2 overflow-x-auto pb-1"
        aria-label="Operational case statistics"
      >
        <QueueStatCard
          count={stats?.byStatus?.new}
          active={queueView === 'untriaged'}
          icon={InboxIcon}
          onClick={() => handleQueueViewChange('untriaged')}
        >Untriaged</QueueStatCard>
        <QueueStatCard
          count={stats?.openCount}
          active={queueView === 'open'}
          icon={FolderOpenIcon}
          onClick={() => handleQueueViewChange('open')}
        >Open Cases</QueueStatCard>
        <QueueStatCard
          count={stats?.awaitingReplyCount}
          active={queueView === 'needs_reply'}
          tone="warning"
          icon={ChatBubbleLeftEllipsisIcon}
          onClick={() => handleQueueViewChange('needs_reply')}
        >Needs Reply</QueueStatCard>
        <QueueStatCard
          count={stats?.criticalOpenCount}
          active={queueView === 'critical'}
          tone="danger"
          icon={ExclamationTriangleIcon}
          onClick={() => handleQueueViewChange('critical')}
        >Critical Open</QueueStatCard>
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm" aria-label="Case filters">
        <button
          type="button"
          onClick={() => setFiltersExpanded((expanded) => !expanded)}
          aria-expanded={filtersExpanded}
          aria-controls="case-filters-content"
          className="flex min-h-touch w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-storm-blue sm:px-5"
        >
          <span className="min-w-0">
            <span className="block font-semibold text-deep-navy">Find a case</span>
            <span className="mt-0.5 block truncate text-xs text-gray-500">
              {activeFilterSummary.length > 1 ? activeFilterSummary.join(' · ') : `All cases · ${activeFilterSummary[0]}`}
            </span>
          </span>
          <ChevronDownIcon
            className={`h-5 w-5 shrink-0 text-gray-400 transition-transform motion-reduce:transition-none ${filtersExpanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>

        {filtersExpanded && (
          <div id="case-filters-content" className="space-y-4 border-t border-gray-100 p-4 sm:p-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <p className="text-xs text-gray-500">Counts show all cases in the selected environment.</p>
              <div className="relative w-full lg:max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  aria-label="Search cases by message, name, or email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by message, name, or email..."
                  className="min-h-touch w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm focus:border-storm-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-storm-blue/20"
                />
              </div>
            </div>

            <fieldset>
              <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Status</legend>
              <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map((option) => (
                  <FilterPill
                    key={option.value}
                    label={option.label}
                    count={option.value === 'all' ? stats?.total : stats?.byStatus?.[option.value]}
                    active={statusFilter === option.value}
                    onClick={() => {
                      setQueueView('all');
                      setStatusFilter(option.value);
                    }}
                  />
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Type</legend>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_FILTERS.map((option) => (
                  <FilterPill
                    key={option.value}
                    label={option.label}
                    count={option.value === 'all' ? stats?.total : stats?.byCategory?.[option.value]}
                    active={categoryFilter === option.value}
                    onClick={() => setCategoryFilter(option.value)}
                  />
                ))}
              </div>
            </fieldset>

            <div className="flex flex-col gap-4 border-t border-gray-100 pt-4 xl:flex-row xl:items-end xl:justify-between">
              <fieldset>
                <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Urgency</legend>
                <div className="flex flex-wrap gap-2">
                  {URGENCY_FILTERS.map((option) => (
                    <FilterPill
                      key={option.value}
                      label={option.label}
                      count={option.value === 'all' ? stats?.total : stats?.byUrgency?.[option.value]}
                      active={urgencyFilter === option.value}
                      onClick={() => {
                        setQueueView('all');
                        setUrgencyFilter(option.value);
                      }}
                    />
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Sort</legend>
                <div className="flex flex-wrap gap-2">
                  <FilterPill label="Awaiting Reply First" active={sortMode === 'awaiting_first'} onClick={() => setSortMode('awaiting_first')} />
                  <FilterPill label="Newest" active={sortMode === 'newest'} onClick={() => setSortMode('newest')} />
                </div>
              </fieldset>
            </div>
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm" aria-label="Case queue">
        {appendError && (
          <div className="m-4 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-800">Failed to load more cases: {appendError}</p>
            <button
              type="button"
              onClick={() => setAppendError(null)}
              className="min-h-touch min-w-touch rounded-lg p-2 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              aria-label="Dismiss load more error"
            >
              <XMarkIcon className="w-4 h-4 text-red-500" />
            </button>
          </div>
        )}

        {loading && caseList.length === 0 ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse border-b border-gray-100 p-4">
              <div className="mb-2 flex gap-2">
                <div className="h-5 w-16 rounded-full bg-gray-200" />
                <div className="h-5 w-12 rounded bg-gray-200" />
              </div>
              <div className="mb-2 h-4 w-full rounded bg-gray-200" />
              <div className="h-4 w-3/4 rounded bg-gray-200" />
            </div>
          ))
        ) : caseList.length === 0 ? (
          <div className="bg-gray-50/70 p-12 text-center">
            <ChatBubbleLeftEllipsisIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <p className="font-medium text-gray-600">No cases found</p>
            <p className="mt-1 text-sm text-gray-400">
              {statusFilter !== 'all' || categoryFilter !== 'all' || urgencyFilter !== 'all' || searchTerm
                ? 'Try adjusting your filters'
                : 'Support cases will appear here'}
            </p>
          </div>
        ) : (
          caseList.map((caseItem) => (
            <Fragment key={caseItem.id}>
              <CaseRow
                caseItem={caseItem}
                onSelect={selectCase}
                isExpanded={selectedCase?.id === caseItem.id}
                panelId={`case-panel-${caseItem.id}`}
              />
              {selectedCase?.id === caseItem.id && (
                <CasePanel
                  caseItem={selectedCase}
                  panelId={`case-panel-${caseItem.id}`}
                  threadState={threadState}
                  onRetryThread={() => fetchThread(selectedCase.id)}
                  onReply={handleReply}
                  sending={sending}
                  onStatusChange={handleStatusChange}
                  onClassificationChange={handleClassificationChange}
                  onSaveInternal={handleSaveInternal}
                  saving={saving}
                  onClose={handleCollapseCase}
                  userDeletedOverride={deletedUserCaseIds.has(selectedCase.id)}
                />
              )}
            </Fragment>
          ))
        )}

        {hasMore && !loading && (
          <div className="border-t border-gray-100 p-4 text-center">
            <button
              type="button"
              onClick={handleLoadMore}
              className="min-h-touch rounded-full bg-blue-50 px-6 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              Load More
            </button>
          </div>
        )}

        {loading && caseList.length > 0 && (
          <div className="border-t border-gray-100 p-4 text-center">
            <p className="text-sm text-gray-500">Loading more...</p>
          </div>
        )}
      </section>
    </div>
  );
}
